import {
	keepPreviousData,
	useInfiniteQuery,
	useMutation,
	useQuery,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { generatePath, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import routing from "routing";
import newApi from "src/newApi";
import { queryClient } from "src/queryClient";

import options from "./defaultOptions";

export const unfinishedExamsKey = "unfinishedExams";
export const finishedExamsKey = "finishedExams";

export function useUnfinishedExams(filters) {
	return useQuery({
		queryKey: [unfinishedExamsKey, filters],

		queryFn: async () => {
			const { data, count } = await newApi.getUnfinishedExams(filters);

			return {
				rows: data,
				count,
			};
		},

		...options,
	});
}

export function useFinishedExams(filters) {
	return useQuery({
		queryKey: [finishedExamsKey, filters],

		queryFn: async () => {
			const { data, count } = await newApi.getFinishedExams(filters);

			return {
				rows: data,
				count,
			};
		},

		...options,
	});
}

export const useRemoveUnfinishedExam = () =>
	useMutation({
		mutationFn: (id) => newApi.removeUnfinishedExams([id]),
		onSuccess: async () => {
			await queryClient.refetchQueries({ queryKey: [unfinishedExamsKey] });
			queryClient.removeQueries({ queryKey: ["folder"] });
			queryClient.removeQueries({ queryKey: ["folder-items"] });
			queryClient.removeQueries({ queryKey: ["folders"] });
		},
	});

export const useRemoveFinishedExam = () =>
	useMutation({
		mutationFn: (id) => newApi.removeFinishedExams([id]),
		onSuccess: async () => {
			await queryClient.refetchQueries({ queryKey: [finishedExamsKey] });
			queryClient.removeQueries({ queryKey: ["folder"] });
			queryClient.removeQueries({ queryKey: ["folder-items"] });
			queryClient.removeQueries({ queryKey: ["folders"] });
		},
	});

export const useRemoveExams = () => {
	return useMutation({
		mutationFn: (ids) => newApi.deleteExams(ids),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: [unfinishedExamsKey] });
			await queryClient.invalidateQueries({ queryKey: [finishedExamsKey] });
			queryClient.removeQueries({ queryKey: ["folder"] });
			queryClient.removeQueries({ queryKey: ["folder-items"] });
			queryClient.removeQueries({ queryKey: ["folders"] });
		},
	});
};

export const useCreateExam = () => {
	const { t } = useTranslation();
	const history = useHistory();

	return useMutation({
		mutationFn: async ({ name, typeDetails, folderIds, ...data }) => {
			const response = await newApi.generateExam(
				data,
				name,
				typeDetails,
				folderIds,
			);

			return {
				...response,
				status: "active",
			};
		},
		onSuccess: async (data) => {
			if (!data.id) {
				toast.error(t("errors.emptyItems"), {
					position: toast.POSITION.BOTTOM_LEFT,
					pauseOnHover: false,
					autoClose: 5000,
				});
				return;
			}
			await queryClient.invalidateQueries({ queryKey: [unfinishedExamsKey] });
			queryClient.removeQueries({ queryKey: ["examPreviewDetails"] });
			queryClient.setQueryData([`exam-${data.id}`], () => data);
			queryClient.removeQueries({ queryKey: ["courses"] });
			queryClient.removeQueries({ queryKey: ["folder"] });
			queryClient.removeQueries({ queryKey: ["folder-items"] });
			queryClient.removeQueries({ queryKey: ["folders"] });

			history.push(
				generatePath(routing.paths.exam, {
					id: data.id,
					questionId: 1,
				}),
			);
		},
		onError: (error) => {
			if (error?.response && error.response.status === 404) {
				toast.error(t("errors.emptyItems"), {
					position: toast.POSITION.BOTTOM_RIGHT,
					pauseOnHover: false,
					autoClose: 2000,
				});
			} else {
				toast.error(t("errors.somethingWentWrong"), {
					position: toast.POSITION.BOTTOM_RIGHT,
					pauseOnHover: false,
					autoClose: 2000,
				});
			}
		},
	});
};

export function useGetAllUserExamsInfinite(filters, enabled = true) {
	return useInfiniteQuery({
		queryKey: ["allUserExamsInfinite", filters],
		queryFn: async ({ pageParam = 0 }) => {
			return newApi.getAllUserExams({
				...filters,
				page: pageParam,
				take: 10,
			});
		},
		initialPageParam: 0,
		...options,
		getNextPageParam: (lastPage, allPages) => {
			const loadedItems = allPages.flatMap((page) => page.data || []).length;
			return loadedItems < lastPage.count ? allPages.length : undefined;
		},
		enabled,
		placeholderData: keepPreviousData,
	});
}
